var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __metadata = (this && this.__metadata) || function (k, v) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(k, v);
};
var _a;
import { Component, Vue, Prop } from 'vue-property-decorator';
import DataTable from '@/components/data-table/DataTable';
import CreatePlanDialog from '@/views/Users/Student/Dialogs/CreatePlanDialog/CreatePlanDialog';
import { StudentDocument, StudentDocumentIncludes } from '@/models/StudentDocument';
import ErrorHandler from '@/support/ErrorHandler';
import { formatDate } from '@/support/String';
import { RolesEnum } from '@/items/Roles';
import { Student as StudentModel } from '@/models/Student';
let Plan = class Plan extends Vue {
    constructor() {
        // File structure for Typescript files in your Vue project
        // https://tutorials.atabix.com/frontend/typscript_file_structure/
        super(...arguments);
        // #endregion
        // #region @Refs
        // #endregion
        // #region Class properties
        this.$pageTitle = 'Student Plan';
        this.isCreatingPlan = false;
        this.selectedPlan = null;
        // #endregion
        // #region @Watchers
        // #endregion
    }
    // #endregion
    // #region Lifecycle Hooks / Init
    // #endregion
    // #region Class methods
    refreshDataTable() {
        document.dispatchEvent(new CustomEvent('datatable:hook', {
            detail: (dataTable) => {
                dataTable.refresh();
            },
        }));
    }
    editPlan(plan) {
        this.selectedPlan = plan;
    }
    getStatusLabel(plan) {
        return plan.is_draft ? this.$tc('student_plan.status.concept') : this.$tc('student_plan.status.published');
    }
    onCreated() {
        this.refreshDataTable();
        this.closeDialog();
    }
    onUpdated() {
        this.refreshDataTable();
    }
    closeDialog() {
        this.isCreatingPlan = false;
        this.selectedPlan = null;
    }
    openConfirmDeleteDialog(plan) {
        this.$store.dispatch('openDialog', this.dialogOptions(plan));
    }
    // #endregion
    // #region Async methods
    // #endregion
    // #region Getters & Setters
    get isPlanDialogOpen() {
        return !!(this.selectedPlan || this.isCreatingPlan);
    }
    set isPlanDialogOpen(value) {
        this.closeDialog();
    }
    get canAddPlan() {
        return this.$store.state.Auth.hasRole([
            RolesEnum.ADMIN,
            RolesEnum.ROOT,
            RolesEnum.TEACHER,
            RolesEnum.TRAINER,
            RolesEnum.MANAGER,
        ]);
    }
    get user() {
        return this.$store.state.Auth;
    }
    get isTeacherWithExpertRights() {
        return this.user && this.user.isTeacherWithExpertRights();
    }
    get tableOptions() {
        return {
            model: new StudentDocument()
                .include([
                StudentDocumentIncludes.STATUS,
                StudentDocumentIncludes.TEACHERS,
                StudentDocumentIncludes.CREATED_BY,
            ])
                .filter({ user: this.student.id }),
            sort: {
                key: 'start_at',
                order: 'DESC',
            },
            name: {
                singular: this.$tc('student_plan.title', 1),
                plural: this.$tc('student_plan.title', 2),
            },
            headers: [
                {
                    text: this.$tc('student_plan.table.title'),
                    value: 'title',
                },
                {
                    text: this.$tc('student_plan.table.description'),
                    value: 'description',
                    transform: (description) => {
                        return description || '-';
                    },
                },
                {
                    text: this.$tc('student_plan.table.start_at'),
                    value: 'start_at',
                    transform: (date) => {
                        return date ? formatDate(date, 'dd-LL-yyyy') : '-';
                    },
                    sortable: {
                        key: 'start_at',
                        order: 'ASC',
                    },
                },
                {
                    text: this.$tc('student_plan.table.end_at'),
                    value: 'end_at',
                    transform: (date) => {
                        return date ? formatDate(date, 'dd-LL-yyyy') : '-';
                    },
                    sortable: {
                        key: 'end_at',
                        order: 'ASC',
                    },
                },
                {
                    text: this.$tc('student_plan.table.status'),
                    value: 'is_draft',
                    transform: (_is_draft, plan) => {
                        return this.getStatusLabel(plan);
                    },
                    sortable: {
                        key: 'is_draft',
                        order: 'ASC',
                    },
                },
            ],
            actions: [
                {
                    type: 'edit',
                    label: 'Download',
                    icon: 'download',
                    action: async (vm, plan) => {
                        if (plan && plan.has_file) {
                            const document = await new StudentDocument()
                                .include(StudentDocumentIncludes.FILE)
                                .find(plan.id);
                            window.open(document.file || '', '_blank');
                        }
                    },
                    visible: (vm, plan) => {
                        return !!(plan && plan.has_file);
                    },
                },
                {
                    type: 'edit',
                    label: 'edit',
                    icon: 'edit',
                    action: (vm, plan) => this.editPlan(plan),
                    visible: (vm, plan) => {
                        const currentUser = this.$store.state.Auth.id;
                        const creatorUser = (plan && plan.created_by && plan.created_by.id) || null;
                        const allowedTeachers = ((plan && plan.teachers) || []).filter((teacher) => teacher.permission === 'edit');
                        return (this.$store.state.Auth.hasRole([RolesEnum.ROOT, RolesEnum.ADMIN]) ||
                            this.isTeacherWithExpertRights ||
                            currentUser === creatorUser ||
                            !!(plan && plan.is_editable_for_teachers) ||
                            allowedTeachers.some((teacher) => teacher.user.id === currentUser));
                    },
                },
                {
                    type: 'delete',
                    label: 'delete',
                    icon: 'delete',
                    action: (vm, plan) => {
                        this.openConfirmDeleteDialog(plan);
                    },
                    visible: (vm, plan) => {
                        return (this.$store.state.Auth.hasRole([RolesEnum.ROOT, RolesEnum.ADMIN]) ||
                            this.isTeacherWithExpertRights ||
                            this.$store.state.Auth.id === (plan && plan.created_by && plan.created_by.id));
                    },
                },
            ],
            filters: [],
        };
    }
    get tableVisibility() {
        return {
            checkboxes: false,
        };
    }
    dialogOptions(plan) {
        return {
            title: this.$t('dialogOptions.confirmation').toString(),
            text: this.$tc('dialogOptions.delete', 1, {
                item: this.$t('dialogOptions.item.project'),
            }).toString(),
            type: 'warning',
            buttons: {
                confirm: {
                    text: this.$t('dialogOptions.button.delete').toString(),
                    action: () => {
                        plan
                            .delete()
                            .then((response) => {
                            if (response) {
                                this.refreshDataTable();
                            }
                        })
                            .catch((error) => {
                            ErrorHandler.network(error);
                        });
                    },
                },
                cancel: {
                    text: this.$t('dialogOptions.button.cancel').toString(),
                },
            },
        };
    }
};
__decorate([
    Prop({ required: true }),
    __metadata("design:type", typeof (_a = typeof StudentModel !== "undefined" && StudentModel) === "function" ? _a : Object)
], Plan.prototype, "student", void 0);
Plan = __decorate([
    Component({
        components: {
            DataTable,
            CreatePlanDialog,
        },
    })
], Plan);
export default Plan;
